import {
  useLayoutEffect,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';

/**
 * @public
 */
export interface ContainerSize {
  /** The border-box width of the container. */
  width: number;
  /** The border-box height of the container. */
  height: number;
  /** The content-box width of the container. */
  contentWidth: number;
  /** The content-box height of the container. */
  contentHeight: number;
}

let resizeObserver: ResizeObserver;
const sizes = new Map<Element, ContainerSize>();
const callbacks = new Map<Element, Dispatch<SetStateAction<ContainerSize>>>();

/**
 * `useContainerSize` is a hook that observes and returns the size of a container
 * on the page.
 * @public
 */
export function useContainerSize(
  /** Reference of the container to observe. */
  ref: MutableRefObject<HTMLElement | null>,
): ContainerSize {
  const [containerSize, setContainerSize] = useState<ContainerSize>({
    width: 0,
    height: 0,
    contentWidth: 0,
    contentHeight: 0,
  });

  useLayoutEffect(() => {
    // If no ref is set, do not run a layout effect.
    if (!ref.current) {
      return;
    }

    if (!callbacks.get(ref.current)) {
      callbacks.set(ref.current, (size) => {
        setContainerSize(size);
      });
    }

    if (!resizeObserver) {
      // Initialize the observer and listen to resize events
      // of the passed ref.
      resizeObserver = new ResizeObserver((entries) => {
        if (entries) {
          for (const entry of entries) {
            if (entry.borderBoxSize === undefined) {
              continue;
            }
            const newHeight = entry.borderBoxSize[0].blockSize;
            const newWidth = entry.borderBoxSize[0].inlineSize;
            const contentHeight = entry.contentRect.height;
            const contentWidth = entry.contentRect.width;

            if (
              newHeight !== sizes.get(entry.target)?.height ||
              newWidth !== sizes.get(entry.target)?.width
            ) {
              const newSize = {
                width: newWidth,
                height: newHeight,
                contentWidth,
                contentHeight,
              };

              sizes?.set(entry.target, newSize);
              callbacks.get(entry.target)?.(newSize);
            }
          }
        }
      });
    }

    // Observe the current ref
    resizeObserver.observe(ref.current);
    const _ref = ref.current;

    // Add cleanup to disconnect the resizeObserver.
    return () => {
      _ref && resizeObserver.unobserve(_ref);
      callbacks.delete(_ref);

      if (callbacks.size === 0) {
        resizeObserver.disconnect();
      }
    };
  });

  return containerSize;
}
